import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { SortingState } from "@tanstack/react-table";

import {
  exportOrganisationsCSV,
  fetchOrganisations as OnfetchOrganisations,
  updateOrganisation as onUpdateStatusInOrg,
  OrganisationsExportsCSV,
} from "slices/organisation/thunk";
import { toast } from "react-toastify";
import TableContainer from "Common/TableContainer";
import { app_modules } from "helpers/apps_modules_list";
import { fetchLatestPermissions } from "slices/auth/login/thunk";
import EditableCell from "./EditableCell";
import LoaderErrorWrapper from "Components/LoaderErrorWrapper/LoaderErrorWrapper";
import SearchBox from "Components/SearchBox/SearchBox";

const OrganisationsList = () => {
  document.title = "Organisation List";
  const dispatch = useDispatch<any>();

  const selectorganisation = createSelector(
    (state: any) => state.Organisations,
    (Org) => ({
      organisation: Org.organisationsList,
      rowCount: Org.rowCount,
      loading: Org.loading,
      error: Org.error,
    })
  );

  const { organisation, rowCount, loading, error } =
    useSelector(selectorganisation);

  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.permissions || [];
  const master_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.user_management?.app_name
  );
  const organisation_permissions =
    master_permissions?.modules_permissions?.find(
      (item: any) =>
        item?.modules_name ===
        app_modules?.user_management?.modules?.organisation
    );

  //*// For redirecting back to this page and get the same paginated pageIndex
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page") || "0");

  const [pageIndex, setPageIndex] = useState(pageParam);
  const [pageSize, setPageSize] = useState(10);
  const [columnFilters, setColumnFilters] = useState<any>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [colValue, setColValue] = useState<any>();
  const [filterOptions, setFilterOptions] = useState<Record<string, any[]>>({});

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  useEffect(() => {
    dispatch(
      OnfetchOrganisations({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: columnFilters,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    columnFilters,
    colValue,
    sortField,
    sortDirection,
  ]);

  //*// All pagination Issues(resolved) when redirecting from the edit or view page
  // Reset pagenumber to 0 when not first render
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPageIndex(0);
  }, [sorting, pageSize, columnFilters, search]);
  const handlePageChange = (newPage: number) => {
    setPageIndex(newPage);
    setSearchParams({ page: newPage.toString() });
  };
  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);
  //*//

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        await dispatch(fetchLatestPermissions()).unwrap();
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
        // toast.error("Failed to fetch latest permissions");
      }
    };
    fetchPermissions();
  }, [dispatch]);

  const handleInlineSave = async (
    rowData: any,
    field: string,
    newValue: string
  ) => {
    try {
      const update_payload = { ...rowData, [field]: newValue };

      await dispatch(onUpdateStatusInOrg(update_payload)).unwrap();

      toast.success(`${field} updated successfully`);

      dispatch(
        OnfetchOrganisations({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: columnFilters,
        })
      );
    } catch (err) {
      toast.error(`Failed to update ${field}`);
    }
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Handle status toggle
 const handleStatusToggle = async (org: any) => {
  try {
    const updatedStatus = !org.org_status;
    const update_payload = { ...org, org_status: updatedStatus };

    await dispatch(onUpdateStatusInOrg(update_payload)).unwrap();

    toast.success(
      `Organisation ${updatedStatus ? "activated" : "deactivated"} successfully`
    );
    dispatch(
      OnfetchOrganisations({
        page: pageIndex + 1,
        pageSize,
        sort: sorting[0]?.id ?? "",
        order: sorting[0]?.desc ? "desc" : "asc",
        search: searchInput,
        filters: columnFilters, // ensures currently applied filters persist
      })
    );
  } catch (err) {
    toast.error("Failed to update status");
  }
};
  const handleExport = async () => {
    try {
      const { blob, filename } = await OrganisationsExportsCSV({
        search,
        filters: columnFilters,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported successfully");
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };
  const DynamicAction = organisation_permissions?.permissions?.Update === true ? [{
    header: "Action",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ row }: { row: any }) => (
      <ul className="d-flex gap-2 list-unstyled mb-0">
        <li>
          <Link
            to={`/edit-organisation/${row.original.organization_id}`}
            state={{ org: row.original }}
            className="btn btn-subtle-secondary btn-icon btn-sm edit-item-btn"
          >
            <i className="ph-pencil"></i>
          </Link>
        </li>
      </ul>
    ),
  },] : []

  // Table columns
  const columns = useMemo(
    () => [
      // {
      //   header: "Sr. No.",
      //   id: "sr_no", // important: since there's no accessorKey
      //   enableSorting: false,
      //   enableColumnFilter: false,
      //   cell: ({ row }: { row: any }) => {
      //     return row.index + 1 + pageIndex * pageSize;
      //   },
      // },
      {
        header: "Organisation",
        accessorKey: "organization_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/organization/column-filter-dropdown/?column_filter=organization_name",
        cell: ({ row, column, getValue }: any) => (
          <EditableCell
            value={getValue()}
            row={row}
            column={column}
            onSave={handleInlineSave}
          />
        ),
      },
      {
        header: "Address",
        accessorKey: "org_address",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "text" as const,
        cell: ({ row, column, getValue }: any) => (
          <EditableCell
            value={getValue()}
            row={row}
            column={column}
            onSave={handleInlineSave}
          />
        ),
      },

      {
        header: "Industry",
        accessorKey: "industry_type",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/organization/column-filter-dropdown/?column_filter=industry_type",
        cell: ({ row, column, getValue }: any) => (
          <EditableCell
            value={getValue()}
            row={row}
            column={column}
            onSave={handleInlineSave}
          />
        ),
      },
      {
        header: "City",
        accessorKey: "org_city",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/organization/column-filter-dropdown/?column_filter=org_city",
        cell: ({ row, column, getValue }: any) => (
          <EditableCell
            value={getValue()}
            row={row}
            column={column}
            onSave={handleInlineSave}
          />
        ),
      },
      {
        header: "State",
        accessorKey: "org_state",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/organization/column-filter-dropdown/?column_filter=org_state",
        cell: ({ row, column, getValue }: any) => (
          <EditableCell
            value={getValue()}
            row={row}
            column={column}
            onSave={handleInlineSave}
          />
        ),
      },
      {
        header: "Country",
        accessorKey: "org_country",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/organization/column-filter-dropdown/?column_filter=org_country",
        cell: ({ row, column, getValue }: any) => (
          <EditableCell
            value={getValue()}
            row={row}
            column={column}
            onSave={handleInlineSave}
          />
        ),
      },
      {
        header: "Pincode",
        accessorKey: "org_pincode",
        enableColumnFilter: false, // explicitly disable
        enableSorting: true,
        cell: ({ row, column, getValue }: any) => (
          <EditableCell
            value={getValue()}
            row={row}
            column={column}
            onSave={handleInlineSave}
          />
        ),
      },
      {
        header: "Status",
        accessorKey: "org_status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const org = row.original;
          return (
            <div
              style={{ fontSize: "1.5rem", cursor: "pointer" }}
              onClick={() => handleStatusToggle(row?.original)}
            >
              <i
                className={`bi ${org.org_status
                    ? "bi-toggle-on text-primary"
                    : "bi-toggle-off text-danger"
                  }`}
              // style={{ color: "#0073BB" }}
              />
            </div>
          );
        },
      },

      {
        header: "Created Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_name ?? "N/A";
          const date = formatDate(row.original.created_at);
          return `  ${name}  -  ${date}  `; // extra spaces for visual padding
        },
      },
      {
        header: "Updated Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_name ?? "N/A";
          const date = formatDate(row.original.updated_at);
          return `  ${name}  -  ${date}  `; // spaces before/after the name
        },
      },
      ...DynamicAction

      // {
      //   header: "Action",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row }: { row: any }) => (
      //     <ul className="d-flex gap-2 list-unstyled mb-0">
      //       <li>
      //         <Link
      //           to={`/edit-organisation/${row.original.organization_id}`}
      //           state={{ org: row.original }}
      //           className="btn btn-subtle-secondary btn-icon btn-sm edit-item-btn"
      //         >
      //           <i className="ph-pencil"></i>
      //         </Link>
      //       </li>
      //     </ul>
      //   ),
      // },
    ],
    [dispatch, organisation, handleStatusToggle]
  );

  return (
    <Fragment>
      <div className="page-content">
        <Container fluid>
          <Row id="invoiceList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2">
                    <Col lg={3} className="me-auto">
                      <h6 className="card-title mb-0">Organisation</h6>
                    </Col>

                    {/* Search Input Box (reusable)*/}
                    <Col xl={2} md={3}>
                      <SearchBox
                        value={searchInput}
                        onChange={setSearchInput}
                      />
                    </Col>

                    <Col className="col-md-auto">
                      <div className="hstack gap-2">
                        <Button
                          className="btn btn-subtle-danger d-none"
                          id="remove-actions"
                        >
                          <i className="ri-delete-bin-2-line"></i>
                        </Button>

                        {
                          organisation_permissions?.permissions?.Create === true &&
                          <Link
                            to="/organisation-form"
                            className="btn btn-primary"
                          >
                            <i className="bi bi-plus-circle align-baseline me-1"></i>
                            Add Organisation
                          </Link>
                        }
                      </div>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  <LoaderErrorWrapper loading={loading} error={error}>
                    <TableContainer
                      columns={columns}
                      data={
                        organisation && organisation.length > 0
                          ? organisation
                          : []
                      }
                      totalRecords={rowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      // onPageChange={setPageIndex}
                      onPageChange={handlePageChange} // for persisted pagination across pages
                      onPageSizeChange={setPageSize}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      colValue={setColValue}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      manualSorting={true}
                      isBordered={false}
                      isPagination={true}
                      isGlobalFilter={false}
                      SearchPlaceholder="Search organisation..."
                    />
                  </LoaderErrorWrapper>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </Fragment>
  );
};

export default OrganisationsList;
